import axios from 'axios'
import { defineStore } from 'pinia'
import { BASE_URL } from '@/environment/environment'
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'
import { usePermissionsStore } from '@/modules/permissions/stores/permissionsStore'
import { useFilesStore } from '@/modules/files/stores/filesStore'
import { toApiDate } from '@/utils/formatters'
import { logger } from '@/utils/eventLogger'
import { isHttpNotFound } from '@/utils/httpErrors'
import { getApiError } from '@/composables/getApiError'

import type { ITicketsStoreState } from '@/modules/tickets/types/ITicketsStoreState'
import type { ICreateTicketPayload } from '@/modules/tickets/types/ICreateTicketPayload'
import type { TicketPriority } from '@/modules/tickets/types/TicketPriority'
import type { IExistingFile } from '@/modules/files/types/IExistingFile'
import type { UploadPhase } from '@/modules/files/types/IUploadPhase'
import type { IRealtimePayload } from '@/modules/centrifuge/types/IRealtimePayload'
import type { ITicketListItem } from '@/modules/tickets/types/ITicketListItem'
import type { IHistoryLog } from '@/types/IHistoryLog'
import type { ITicketsFilters } from '@/modules/tickets/types/ITicketsFilters'
import type { ITicketCategory } from '@/modules/tickets/types/ITicketCategory'
import type { ITreeNode } from '@/modules/apiStore/types/ITreeNode'
import type { ITicketService } from '@/modules/tickets/types/ITicketService'
import type { IUniversalTreeSelectModule } from '@/types/IUniversalTreeSelectModule'
export const useTicketsStore = defineStore('ticketsStore', {
  state: (): ITicketsStoreState => ({
    ticketLogs: [] as IHistoryLog[],
    tickets: [],
    nextCursor: null,
    currentTicket: {
      description: '',
      contact_number: '',
      is_high_priority: false,
      location: null,
      location_str: '',
      department: null,
      category: null,
      service: null,
    } as ICreateTicketPayload,
    currentSortString: null,
    filterChoices: {},
    searchChoices: {},
    filterCursors: {},
    filterLoading: {},
    filterStrategies: {},
    editableTicket: null,
    totalTicketsCount: 0,
    itemsPerPage: 25,
    services: [] as ITicketService[],
    categoryTree: [] as ITreeNode[],
    servicesTree: [] as ITreeNode[],
    filters: {
      search: '',
      categories: [],
      coordinators: [],
      priorities: [],
      // Завершённые скрыты по умолчанию переключателем «Скрыть завершённые» (is_actual), а не фильтром статусов
      status: [],
      created_after: null,
      created_before: null,
      is_active: null,
      requires_action: null,
      is_actual: true,
      services: [],
      departments: [],
      numbers: [],
      creators: [],
    } as ITicketsFilters,
    uploadNewFiles: [],
    uploadRemovedFileIds: [],
  }),

  actions: {
    /**
     * Загрузка вариантов фильтрации для UniversalFilterTree (модуль Тикеты)
     * Эндпоинт: /api/ticket/ticket/filter-choices/
     * Добавлена отправка активных фильтров (контекст) и замена search на search_field
     */
    async fetchFilterChoices(fieldKey: string, search = '', cursor?: string, module?: IUniversalTreeSelectModule) {
      if (!fieldKey) return
      this.filterLoading[fieldKey] = true

      try {
        const params: any = { field: fieldKey }

        // 1. Изменение параметра поиска
        if (search) params.search_field = search

        if (cursor) params.cursor = cursor

        // 2. Подмешивание активных фильтров (исключая текущее поле fieldKey)
        const filters = this.filters as ITicketsFilters

        // Статус
        if (fieldKey !== 'status' && filters.status.length) {
          params.status = filters.status.join(',')
        }

        // Переключатель «Скрыть завершённые»
        if (filters.is_actual !== null && filters.is_actual !== undefined) {
          params.is_actual = filters.is_actual
        }

        // Приоритеты
        if (fieldKey !== 'priorities' && filters.priorities.length) {
          params.priorities = filters.priorities.join(',')
        }

        // Категории
        if (fieldKey !== 'categories' && filters.categories.length) {
          params.categories = filters.categories.join(',')
        }

        // Службы
        if (fieldKey !== 'services' && filters.services.length) {
          params.services = filters.services.join(',')
        }

        // Отделения
        if (fieldKey !== 'departments' && filters.departments.length) {
          params.departments = filters.departments.join(',')
        }

        // Регистраторы (creators)
        if (fieldKey !== 'creators' && filters.creators.length) {
          params.creators = filters.creators.join(',')
        }

        // Номера
        if (fieldKey !== 'numbers' && filters.numbers.length) {
          params.numbers = filters.numbers.join(',')
        }

        // Координаторы
        if (fieldKey !== 'coordinators' && filters.coordinators.length) {
          params.coordinators = filters.coordinators.join(',')
        }

        // Активные
        if (fieldKey !== 'is_active' && filters.is_active !== null) {
          params.is_active = filters.is_active
        }

        // Даты (если они не являются текущим полем фильтрации, хотя для дат обычно нет filter-choices, но для надежности)
        if (fieldKey !== 'created_after' && filters.created_after) {
          params.created_after = toApiDate(filters.created_after)
        }
        if (fieldKey !== 'created_before' && filters.created_before) {
          params.created_before = toApiDate(filters.created_before)
        }

        // Общий поиск (если он введен в главном поле)
        if (filters.search) {
          params.search = filters.search
        }

        const response = await axios.get(`${BASE_URL}/api/ticket/ticket/filter-choices/`, { params })
        const { results, next, strategy } = response.data

        this.filterStrategies[fieldKey] = strategy || 'independent'

        if (search) {
          if (cursor) {
            this.searchChoices[fieldKey] = [...(this.searchChoices[fieldKey] || []), ...results]
          } else {
            this.searchChoices[fieldKey] = results
          }
        } else {
          if (cursor) {
            this.filterChoices[fieldKey] = [...(this.filterChoices[fieldKey] || []), ...results]
          } else {
            this.filterChoices[fieldKey] = results
          }
        }
        this.filterCursors[fieldKey] = next
      } catch (e) {
        console.error(`Ошибка загрузки фильтров тикетов [${fieldKey}]:`, e)
      } finally {
        this.filterLoading[fieldKey] = false
      }
    },

    /**
     * Загружает список тикетов с сервера
     *
     * Основные действия:
     * - Формирует параметры фильтрации (search, status, priorities, categories, services, departments, creators, dates, is_active)
     * - Отправляет GET-запрос к API
     * - Обновляет стор:
     *    - this.tickets (добавляет к текущему списку или заменяет при первом запросе)
     *    - this.nextCursor
     *    - this.totalTicketsCount
     *    - this.itemsPerPage (если передан новый параметр)
     *
     * @param {string} [cursor] - Курсор для подгрузки следующей страницы
     * @param {number} [itemsPerPage] - Количество элементов на странице для первого запроса или при смене itemsPerPage
     * @throws {Error} При ошибке сетевого запроса или API
     */
    async fetchTickets(cursor?: string, itemsPerPage?: number, throwOnError = false): Promise<void> {
      const feedbackStore = useFeedbackStore()
      if (this.tickets.length === 0) {
        feedbackStore.isGlobalLoading = true
      }
      try {
        const params: Record<string, any> = {}

        // Добавляем sort ТОЛЬКО если он был активирован
        if (this.currentSortString) {
          params.sort = this.currentSortString
        }

        if (itemsPerPage) {
          this.itemsPerPage = itemsPerPage
        }

        if (this.itemsPerPage) {
          params.page_size = this.itemsPerPage
        }

        if (cursor) params.cursor = cursor

        const filters = this.filters as ITicketsFilters

        if (filters.search) params.search = filters.search

        if (filters.status.length) {
          params.status = filters.status.join(',')
        }

        if (filters.priorities.length) {
          params.priorities = filters.priorities.join(',')
        }

        if (filters.categories.length) {
          params.categories = filters.categories.join(',')
        }

        if (filters.services.length) {
          params.services = filters.services.join(',')
        }

        if (filters.departments.length) {
          params.departments = filters.departments.join(',')
        }

        if (filters.creators.length) {
          params.creators = filters.creators.join(',')
        }

        if (filters.numbers.length) {
          params.numbers = filters.numbers.join(',')
        }

        if (filters.is_active !== null) {
          params.is_active = filters.is_active
        }

        if (filters.requires_action !== null && filters.requires_action !== undefined) {
          params.requires_action = filters.requires_action
        }

        if (filters.is_actual !== null && filters.is_actual !== undefined) {
          params.is_actual = filters.is_actual
        }

        if (filters.coordinators.length) {
          params.coordinators = filters.coordinators.join(',')
        }

        if (filters.created_after) {
          params.created_after = toApiDate(filters.created_after)
        }

        if (filters.created_before) {
          params.created_before = toApiDate(filters.created_before)
        }

        const res = await axios.get(`${BASE_URL}/api/ticket/ticket/`, { params })
        const results: ITicketListItem[] = res.data.results ?? res.data

        this.nextCursor = res.data.next

        if (cursor) {
          this.tickets = [...this.tickets, ...results]
        } else {
          this.tickets = results
        }

        this.totalTicketsCount = res.data.count
      } catch (error) {
        if (throwOnError) throw error
        console.error(`❌ Ошибка загрузки заявок: ${String(error)}`)
        const feedbackStore = useFeedbackStore()
        feedbackStore.showToast({ type: 'error', message: getApiError(error, 'Не удалось загрузить заявки') })
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    /**
     * Загружает тикет по id
     */
    async fetchTicketById(id: number): Promise<void> {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true
      try {
        const res = await axios.get<ICreateTicketPayload>(`${BASE_URL}/api/ticket/ticket/${id}/`)
        this.currentTicket = res.data
        this.editableTicket = JSON.parse(JSON.stringify(this.currentTicket))
      } catch (error) {
        feedbackStore.showToast({ type: 'error', message: getApiError(error, 'Не удалось загрузить тикет') })
        if (isHttpNotFound(error))
          throw error
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    /**
     * Создает новый тикет
     */
    async createTicket(payload: ICreateTicketPayload, equipment?: { id: string; command: string }) {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true
      try {
        const res = equipment
          ? await axios.post<ICreateTicketPayload>(`${BASE_URL}/api/equipment/devices/${equipment.id}/create-related/`, { kind: 'ticket', command_id: equipment.command, payload })
          : await axios.post<ICreateTicketPayload>(`${BASE_URL}/api/ticket/ticket/`, payload)
        this.currentTicket = res.data
        await this.fetchTickets()
        feedbackStore.showToast({ type: 'success', message: 'Заявка создана' })
        return res.data
      } catch (error) {
        logger.error('createTicket_error', {
          store: 'ticketsStore',
          function: 'createTicket',
          error: `❌ Ошибка создания заявки: ${error}`,
          payload,
        })
        feedbackStore.showToast({ type: 'error', message: getApiError(error, 'Не удалось создать заявку') })
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    async updateTicket(id: number, payload: ICreateTicketPayload) {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true
      try {
        const res = await axios.patch(`${BASE_URL}/api/ticket/ticket/${id}/`, payload)
        this.currentTicket = res.data
        this.editableTicket = JSON.parse(JSON.stringify(this.currentTicket))
        feedbackStore.showToast({ type: 'success', message: 'Заявка обновлена' })
        return res.data
      } catch (error) {
        logger.error('updateTicket_error', {
          store: 'ticketsStore',
          function: 'updateTicket',
          error: `❌ Ошибка обновления заявки: ${error}`,
          payload,
        })
        feedbackStore.showToast({ type: 'error', message: getApiError(error, 'Не удалось обновить заявку') })
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    resetFilters(): void {
      this.filters = {
        search: '',
        categories: [],
        coordinators: [],
        priorities: [],
        status: [],
        created_after: null,
        created_before: null,
        is_active: null,
        requires_action: null,
        services: [],
        departments: [],
        creators: [],
        numbers: [],
      }
    },

    // [ADD] Файлы: задать все новые файлы из raw-модели компонента
    setNewTicketFiles(raw: Array<File | { file?: File }>): void {
      const files = useFilesStore().normalizeToFileArray(raw)
      this.uploadNewFiles = files
    },
    // [ADD] Очистить буфер новых файлов
    clearNewTicketFiles(): void {
      this.uploadNewFiles = []
    },
    // [ADD] Пометить файл на удаление
    markTicketFileForRemoval(id: number): void {
      if (!this.uploadRemovedFileIds.includes(id)) this.uploadRemovedFileIds.push(id)
      // [ADD] мгновенно обновляем UI
      const cur = this.currentTicket as any
      if (cur && Array.isArray(cur.files)) cur.files = cur.files.filter((f: any) => f.id !== id)
    },
    // [ADD] Очистить метки на удаление
    clearRemovedTicketFiles(): void {
      this.uploadRemovedFileIds = []
    },

    /**
     * Синхронизирует файлы тикета
     */
    async syncTicketFiles(ticketId: number, phase: UploadPhase = 'update'): Promise<void> {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true
      try {
        const filesStore = useFilesStore()
        const hasNew = this.uploadNewFiles.length > 0
        const hasDel = this.uploadRemovedFileIds.length > 0
        if (!hasNew && !hasDel) return

        if (hasNew) await filesStore.uploadTicketFiles(ticketId, this.uploadNewFiles, phase)
        if (hasDel) {
          for (const fid of this.uploadRemovedFileIds) {
            await filesStore.deleteTicketFile(ticketId, fid)
          }
        }
        this.clearNewTicketFiles()
        this.clearRemovedTicketFiles()
      } catch (error) {
        console.error(`❌ Ошибка синхронизации файлов: ${error}`)
        feedbackStore.showToast({ type: 'error', message: getApiError(error, 'Не удалось синхронизировать файлы') })
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    /**
     * Переводит тикет в статус "в работе" для координатора
     */
    async takeTicketOnWork(payload: { id: number; entry_id: number }): Promise<void> {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true
      try {
        const response = await axios.post(
          `${BASE_URL}/api/ticket/ticket/${payload.id}/responsibility-entries/${payload.entry_id}/in-progress/`,
        )

        const updatedCoordinatorEntry = response.data

        // Обновляем только coordinator_entry в текущем событии
        if (Number(this.currentTicket?.id) === payload.id) {
          this.currentTicket.coordinator_entry = updatedCoordinatorEntry
        }

        // Обновляем только coordinator_entry в редактируемой копии
        if (this.editableTicket && Number(this.editableTicket.id) === payload.id) {
          this.editableTicket.coordinator_entry = updatedCoordinatorEntry
        }

        feedbackStore.showToast({ type: 'success', message: 'Заявка переведена в работу' })
      } catch (error) {
        logger.error('takeTicketOnWork_error', {
          store: 'ticketsStore',
          function: 'takeTicketOnWork',
          error: `❌ Ошибка перевода заявки в работу: ${error}`,
          payload,
        })
        feedbackStore.showToast({ type: 'error', message: getApiError(error, 'Не удалось перевести заявку в работу') })
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    /**
     * Возвращает тикет в работу
     */
    async returnTicketOnWork(payload: { id: number; entry_id: number }): Promise<void> {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true
      try {
        const response = await axios.post(
          `${BASE_URL}/api/ticket/ticket/${payload.id}/responsibility-entries/${payload.entry_id}/return/`,
        )

        const returnedCoordinatorEntry = response.data

        // Обновляем только coordinator_entry в текущем событии
        if (Number(this.currentTicket?.id) === payload.id) {
          this.currentTicket.coordinator_entry = returnedCoordinatorEntry
        }

        // Обновляем только coordinator_entry в редактируемой копии
        if (this.editableTicket && Number(this.editableTicket.id) === payload.id) {
          this.editableTicket.coordinator_entry = returnedCoordinatorEntry
        }

        feedbackStore.showToast({ type: 'success', message: 'Заявка возвращена в работу' })
      } catch (error) {
        logger.error('returnTicketOnWork_error', {
          store: 'ticketsStore',
          function: 'returnTicketOnWork',
          error: `❌ Ошибка возвращения заявки в работу: ${error}`,
          payload,
        })
        feedbackStore.showToast({ type: 'error', message: getApiError(error, 'Не удалось вернуть заявку в работу') })
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    /**
     * Завершает тикет для координатора
     */
    async completeTicket(payload: {
      id: number
      entry_id: number
      report?: string
      files?: File[]
    }): Promise<void> {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true

      try {
        const formData = new FormData()

        // Добавляем отчет если есть
        if (payload.report) {
          formData.append('report', payload.report)
        }

        // Добавляем файлы если есть
        if (payload.files && payload.files.length > 0) {
          payload.files.forEach(file => {
            formData.append('files', file)
          })
        }

        const response = await axios.post(
          `${BASE_URL}/api/ticket/ticket/${payload.id}/responsibility-entries/${payload.entry_id}/complete/`,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          },
        )

        const updatedEvent = response.data

        // Обновляем только coordinator_entry в текущем событии
        if (Number(this.currentTicket?.id) === payload.id) {
          this.currentTicket.coordinator_entry = updatedEvent
        }

        // Обновляем только coordinator_entry в редактируемой копии
        if (this.editableTicket && Number(this.editableTicket.id) === payload.id) {
          this.editableTicket.coordinator_entry = updatedEvent
        }

        feedbackStore.showToast({ type: 'success', message: 'Заявка завершена' })
      } catch (error) {
        logger.error('ticketComplete_error', {
          store: 'ticketsStore',
          function: 'completeTicket',
          error: `❌ Ошибка завершения заявки: ${error}`,
          payload,
        })
        feedbackStore.showToast({ type: 'error', message: getApiError(error, 'Не удалось завершить заявку') })
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    /**
     * ticket_created / updated / deleted.
     * Строка таблицы берётся из сообщения: это TicketListSerializer, те же поля, что у таблицы.
     * Открытая форма из сообщения не заполняется: в списке нет файлов и исполнителей, её берём GET по id.
     */
    async applyTicketRealtime(payload: IRealtimePayload): Promise<void> {
      const feedbackStore = useFeedbackStore()
      if (!payload?.event_type || !payload?.data) return

      const ticket = payload.data as ITicketListItem
      const idx = this.tickets.findIndex((t: ITicketListItem) => t.id === ticket.id)
      const isCurrentTicket = Number(this.editableTicket?.id ?? this.currentTicket?.id) === Number(ticket.id)

      if (this.hasActiveFilters) {
        await this.fetchTickets(undefined, undefined, false)
        if (isCurrentTicket) {
          try {
            const detailRes = await axios.get(`${BASE_URL}/api/ticket/ticket/${ticket.id}/`)
            this.currentTicket = detailRes.data
            this.editableTicket = JSON.parse(JSON.stringify(this.currentTicket))
            await this.fetchTicketLogs(Number(ticket.id))
          } catch (error) {
            console.error('applyTicketRealtime_error:', error)
          }
        }
        return
      }

      switch (payload.event_type) {
        case 'ticket_created':
          if (idx >= 0) this.tickets.splice(idx, 1)
          this.tickets = [ticket, ...this.tickets]
          this.totalTicketsCount++
          feedbackStore.showToast({
            type: 'info',
            message: `Получена заявка № ${ticket.number}`,
            time: 5000,
          })
          break
        case 'ticket_updated':
          if (idx >= 0) {
            this.tickets.splice(idx, 1, ticket)
          } else {
            this.tickets = [ticket, ...this.tickets]
          }
          if (isCurrentTicket) {
            try {
              const detailRes = await axios.get(`${BASE_URL}/api/ticket/ticket/${ticket.id}/`)
              this.currentTicket = detailRes.data
              this.editableTicket = JSON.parse(JSON.stringify(this.currentTicket))
              await this.fetchTicketLogs(Number(ticket.id))
            } catch (error) {
              console.error('applyTicketRealtime_error:', error)
            }
          }
          break
        case 'ticket_deleted':
          if (idx >= 0) {
            this.tickets.splice(idx, 1)
            this.totalTicketsCount = Math.max(0, this.totalTicketsCount - 1)
            feedbackStore.showToast({
              type: 'warning',
              message: `Заявка № ${ticket.number} удалена`,
              time: 5000,
            })
          }
          break
      }
    },

    /**
     * Обработка realtime-событий записей ответственности (responsibility_entry_*) для открытой заявки.
     * Payload Centrifuge не пишем: в нём actions пустой. Карточку заново берём с GET.
     */
    async applyResponsibilityEntryRealtime(payload: IRealtimePayload | any): Promise<void> {
      if (!payload?.event_type || !payload?.data) return
      const entry = payload.data as any
      if (entry.source_type !== 'ticket') return

      const ticketId = Number(entry.url_props?.ticketId)
      const currentId = Number(this.editableTicket?.id ?? this.currentTicket?.id)
      if (!ticketId || currentId !== ticketId) return

      await this.fetchTicketById(ticketId)
    },

    async fetchNewCategories(): Promise<void> {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true
      try {
        const [servicesRes, categoriesRes] = await Promise.all([
          axios.get(`${BASE_URL}/api/ticket/service/`),
          axios.get(`${BASE_URL}/api/ticket/category/`, { params: { is_active: true } }),
        ])

        const servicesResults = servicesRes.data?.results ?? servicesRes.data ?? []
        this.services = Array.isArray(servicesResults) ? servicesResults : []

        // Создаем дерево служб для NewTreeComponent
        this.servicesTree = this.buildServicesTree(this.services)

        const categoriesResults = categoriesRes.data?.results ?? categoriesRes.data ?? []
        const categories = Array.isArray(categoriesResults) ? categoriesResults : []

        // Создаем дерево категорий для FilterTreeSelect
        this.categoryTree = this.buildCategoryTree(categories)
      } catch (error) {
        console.error(`❌ Ошибка загрузки категорий: ${error}`)
        feedbackStore.showToast({ type: 'error', message: getApiError(error, 'Не удалось загрузить категории') })
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    /** Загружает дерево служб заявок (GET /api/ticket/service/) */
    async fetchServicesTree(): Promise<ITreeNode[]> {
      const feedbackStore = useFeedbackStore()
      try {
        const response = await axios.get(`${BASE_URL}/api/ticket/service/`)
        const servicesResults = response.data?.results ?? response.data ?? []
        this.services = Array.isArray(servicesResults) ? servicesResults : []
        this.servicesTree = this.buildServicesTree(this.services)
        return this.servicesTree
      } catch (error) {
        console.error('❌ Ошибка загрузки служб заявок:', error)
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось загрузить службы заявок'),
          time: 5000,
        })
        this.services = []
        this.servicesTree = []
        return []
      }
    },

    buildCategoryTree(categories: ITicketCategory[]): ITreeNode[] {
      return categories.map(category => {
        const categoryNode: ITreeNode = {
          key: `category-${category.id}`,
          label: category.name,
          data: {
            name: category.name,
            position: 0,
            selfId: category.id.toString(),
            serviceId: category.service_id ?? undefined,
          },
          selectable: true,
          children: [],
        }

        if (category.children && category.children.length > 0) {
          categoryNode.children = this.buildSubcategories(category.children, category.service_id ?? undefined)
        }

        return categoryNode
      })
    },

    buildSubcategories(categories: ITicketCategory[], parentServiceId?: string): ITreeNode[] {
      return categories.map(category => {
        const currentServiceId = category.service_id || parentServiceId

        return {
          key: `category-${category.id}`,
          label: category.name,
          data: {
            name: category.name,
            position: 1,
            selfId: category.id.toString(),
            serviceId: currentServiceId,
          },
          icon: 'pi pi-tag',
          selectable: true,
          children: category.children && category.children.length > 0
            ? this.buildSubcategories(category.children, currentServiceId)
            : [],
        }
      })
    },

    buildServicesTree(services: ITicketService[]): ITreeNode[] {
      return services.map(service => {
        const node: ITreeNode = {
          key: service.id,
          label: service.short_name || service.name,
          data: {
            name: service.name,
            selfId: service.id,
          },
          selectable: true,
          children: [],
        }

        if (service.children && service.children.length > 0) {
          node.children = this.buildServicesTree(service.children)
        }

        return node
      })
    },


    /**
     * Изменяет ответственного координатора по заявке.
     *
     * @param {Object} payload Параметры запроса с ID заявки и ID назначаемого координатора
     * @param {number} payload.event_id Идентификатор заявки
     * @param {string} payload.coordinator_id Идентификатор сотрудника-координатора
     */
    async changeTicketCoordinator(payload: {
      event_id: number
      coordinator_id: string
    }): Promise<any> {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true

      try {
        const response = await axios.patch(
          `${BASE_URL}/api/ticket/ticket/${payload.event_id}/responsibility-entries/change-coordinator/`,
          { coordinator_id: payload.coordinator_id },
        )

        const updatedCoordinatorEntry = response.data

        if (Number(this.currentTicket?.id) === payload.event_id)
          this.currentTicket.coordinator_entry = updatedCoordinatorEntry

        /* Эталон для hasFormChanges — только coordinator_entry, тело заявки не трогаем */
        if (this.editableTicket && Number(this.editableTicket.id) === payload.event_id)
          this.editableTicket.coordinator_entry = updatedCoordinatorEntry

        feedbackStore.showToast({
          type: 'success',
          message: 'Координатор успешно изменён',
          time: 7000,
        })
        return updatedCoordinatorEntry
      } catch (error) {
        logger.error('changeTicketCoordinator_error', {
          file: 'ticketsStore',
          function: 'changeTicketCoordinator',
          condition: `❌ Ошибка при изменении координатора: ${error}`,
        })
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось изменить координатора'),
          time: 7000,
        })
        throw error
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    /**
     * Загружает историю изменений для указанного тикета
     * @param ticketId - ID тикета
     */
    async fetchTicketLogs(ticketId: number): Promise<IHistoryLog[]> {
      if (!usePermissionsStore().canViewChangeHistory) {
        this.ticketLogs = []
        return []
      }
      const response = await axios.get(`${BASE_URL}/api/ticket/ticket/logs/?ticket_id=${ticketId}`)
      let logs: IHistoryLog[] = []
      if (Array.isArray(response.data)) {
        logs = response.data
      } else if (response.data && Array.isArray(response.data.results)) {
        logs = response.data.results
      }
      this.ticketLogs = logs
      return logs
    },

    updateRequiresAction(id: number, requiresAction: boolean) {
      const ticket = this.tickets.find(t => Number(t.id) === id)
      if (ticket) {
        ticket.requires_action = requiresAction
      }
      // Серверное поле, не правка пользователя — пишем и в эталон, иначе hasFormChanges увидит «несохранённые изменения»
      if (this.currentTicket && Number((this.currentTicket as any).id) === id) {
        (this.currentTicket as any).requires_action = requiresAction
      }
      if (this.editableTicket && Number((this.editableTicket as any).id) === id) {
        (this.editableTicket as any).requires_action = requiresAction
      }
    },
  },
  getters: {
    hasActiveFilters(state): boolean {
      const f = state.filters
      if (!f) return false
      if (f.search && f.search.trim() !== '') return true
      if (f.created_after !== null || f.created_before !== null) return true
      if (f.is_active !== null && f.is_active !== undefined) return true
      if (f.requires_action !== null && f.requires_action !== undefined) return true
      if (Array.isArray(f.categories) && f.categories.length > 0) return true
      if (Array.isArray(f.coordinators) && f.coordinators.length > 0) return true
      if (Array.isArray(f.creators) && f.creators.length > 0) return true
      if (Array.isArray(f.departments) && f.departments.length > 0) return true
      if (Array.isArray(f.numbers) && f.numbers.length > 0) return true
      if (Array.isArray(f.priorities) && f.priorities.length > 0) return true
      if (Array.isArray(f.services) && f.services.length > 0) return true
      if (Array.isArray(f.status) && f.status.length > 0) return true
      return false
    },

    /**
     * Опции приоритета тикетов
     *
     * Используется:
     * - Для отображения выпадающего списка приоритета в формах
     */
    priorityOptions: (): Array<{ label: string; value: TicketPriority }> => [
      { label: 'Критичный', value: 3 as TicketPriority },
      { label: 'Высокий', value: 2 as TicketPriority },
      { label: 'Средний', value: 1 as TicketPriority },
      { label: 'Низкий', value: 0 as TicketPriority },
    ],

    /**
     * Список существующих файлов текущего тикета
     *
     * Основные действия:
     * - Извлекает файлы из текущего тикета
     * - Приводит их к типу `IExistingFile`
     *
     * Используется:
     * - Для отображения файлов в UI
     * - Для дальнейшей синхронизации с API
     */
    existingFilesForCurrent(state): IExistingFile[] {
      const list = (state.currentTicket as any)?.files ?? []
      return list.map((f: any) => ({ id: f.id, file: f.file }))
    },

  },
})
